/**
 * debt-forms (US-AIF.P3-0256 · @UC9442 @UC9443 @UC9444 · @gate money,ai) — FORM NỢ qua chat.
 *
 * BA usecase ĐƯỢC THỰC THI (mỗi cái nối MỘT endpoint nợ ĐÃ CÓ — KHÔNG viết lại engine nợ):
 *  - DEBT-PAY-BEHALF (@UC9442): trả hộ khoản thu của người khác → `PayOnBehalfService.propose`
 *    (POST /v1/member-dues/:id/pay-on-behalf `:56`). 2 PHÍA: người được trả hộ phải `respond` mới ghi sổ.
 *  - DEBT-REMIND    (@UC9443): nhắc người chưa đóng một ĐỢT THU → `SendRemindersService.send`
 *    (POST /v1/collections/:id/reminders `:37`). KHÔNG ledger (outbox + notification).
 *  - DEBT-CONFIRM   (@UC9444): XÁC NHẬN 2 PHÍA một dàn xếp nợ đang chờ → `PeerDebtSettleService.respond`
 *    (POST /v1/funds/:id/debts/settlements/:settlementId/respond `:69`). CHỈ chủ nợ được phản hồi.
 *
 * 🔴 "GHI NỢ" (@UC9441) CỐ TÌNH KHÔNG CÓ Ở ĐÂY — MODEL GAP có bằng chứng, KHÔNG bịa endpoint:
 *  `POST /funds/:id/debts/settle` KHÔNG phải primitive "ghi nợ tuỳ ý". Nó là CLAIM-TRẢ-NỢ do CON NỢ khởi
 *  xướng: controller ÉP `debtorMemberId = ctx.groupMemberId` ("KHÔNG tin client gửi" —
 *  `peer-debt-settle.controller.ts:57-59`) và service ném 409 khi CHƯA CÓ nợ ròng
 *  (`peer-debt-settle.service.ts:193` "Không còn nợ với thành viên này để tất toán"). Nợ peer chỉ SINH RA
 *  như HỆ QUẢ của khoản chi (`on_behalf`/`expense_split`) — đã ship ở P2-0254 (EXP-P-ADVANCE-EQUAL /
 *  EXP-P-PAY-FOR-ONE). ⇒ Câu "ghi nợ" được ROUTE sang deep-link GUI/EXP-P (không ngõ cụt), KHÔNG mở form
 *  ghi nợ giả. Đây là quyết định "chat KHÔNG bịa năng lực không tồn tại" (DP).
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now. Money = string minor VND (B1 — no-float, >0).
 * MỌI options (đợt thu / khoản nợ / dàn xếp chờ) do SERVICE nạp từ DB THẬT (netting/dues) — domain chỉ
 * chốt value ∈ options (IDOR ở LỚP domain; service re-verify RBAC/tenant THẬT lúc confirm).
 *
 * 🔴 KHÔNG SUY CHỦ NỢ/CON NỢ TỪ NGƯỜI ĐANG THAO TÁC: mọi form dưới đây bắt user CHỌN TƯỜNG MINH đối
 * tượng (dueId của người được trả hộ / settlementId cụ thể) từ danh sách THẬT — không có nhánh nào
 * "đoán" actor là chủ nợ hay con nợ.
 */
import { DomainError } from "../errors.js";
import {
  validateFieldValue,
  type SlotFieldSpec,
  type SlotOption,
  type FormState,
} from "./form-slot-filling.js";

/** intentCode — khớp EXACT capability-catalog + router (lệch ⇒ gate coverage bỏ sót âm thầm). */
export const DEBT_PAY_BEHALF_INTENT_CODE = "DEBT-PAY-BEHALF";
export const DEBT_REMIND_INTENT_CODE = "DEBT-REMIND";
export const DEBT_CONFIRM_INTENT_CODE = "DEBT-CONFIRM";

/** specVersion (tăng khi đổi chuỗi field — session lưu để phát hiện drift). */
export const DEBT_PAY_BEHALF_SPEC_VERSION = 1;
export const DEBT_REMIND_SPEC_VERSION = 1;
export const DEBT_CONFIRM_SPEC_VERSION = 1;

/** Key field. */
export const DEBT_DUE_KEY = "memberDueId";
export const DEBT_COLLECTION_KEY = "collectionId";
export const DEBT_SETTLEMENT_KEY = "settlementId";
export const DEBT_DECISION_KEY = "decision";

/** Quyết định 2 phía (đồng `PEER_SETTLEMENT_DECISION` contracts — value khớp EXACT). */
export const DEBT_DECISION_ACCEPT = "accept";
export const DEBT_DECISION_DECLINE = "decline";

/**
 * Thứ tự field (tất-định = thứ tự hỏi).
 *  - PAY-BEHALF: chỉ CHỌN due của NGƯỜI KHÁC (số tiền LẤY TỪ due — KHÔNG cho gõ tay: due.amountMinor là
 *    nguồn sự thật, gõ tay = cơ hội lệch tiền).
 *  - REMIND: chọn ĐỢT THU (collection). Service nhắc mọi người chưa đóng của đợt đó.
 *  - CONFIRM: chọn dàn xếp đang chờ + quyết định accept/decline.
 */
export const DEBT_PAY_BEHALF_FIELD_ORDER = [DEBT_DUE_KEY] as const;
export const DEBT_REMIND_FIELD_ORDER = [DEBT_COLLECTION_KEY] as const;
export const DEBT_CONFIRM_FIELD_ORDER = [DEBT_SETTLEMENT_KEY, DEBT_DECISION_KEY] as const;

/** Payload đã validate — service nối thẳng vào engine tương ứng. */
export interface DebtPayBehalfPayload {
  /** due của NGƯỜI KHÁC (options do service nạp: due unpaid của member ≠ actor, cùng quỹ actor thấy). */
  readonly memberDueId: string;
}

export interface DebtRemindPayload {
  readonly collectionId: string;
}

export interface DebtConfirmPayload {
  readonly settlementId: string;
  readonly decision: typeof DEBT_DECISION_ACCEPT | typeof DEBT_DECISION_DECLINE;
}

/** Ném DomainError REQUIRED_FIELD_MISSING cho field bắt buộc còn trống. */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError("REQUIRED_FIELD_MISSING", `Thiếu thông tin bắt buộc: ${field}.`);
  }
  return value.trim();
}

/**
 * Chốt value ∈ options (IDOR LỚP DOMAIN). Options RỖNG ⇒ luôn ném — FAIL-CLOSED có chủ đích: không có
 * khoản nợ/đợt thu THẬT nào ⇒ KHÔNG được confirm bừa (thà chặn còn hơn ghi nhầm tiền).
 */
function assertInOptions(
  value: string,
  options: readonly SlotOption[],
  message: string,
): string {
  const allowed = new Set(options.map((o) => o.value));
  if (!allowed.has(value)) {
    throw new DomainError("REQUIRED_FIELD_MISSING", message);
  }
  return value;
}

// ─── DEBT-PAY-BEHALF (@UC9442) ────────────────────────────────────────────────────────────────────

/**
 * Field DEBT-PAY-BEHALF: CHỈ chọn due (single_choice trên due THẬT chưa đóng của NGƯỜI KHÁC).
 * 🔴 KHÔNG có field money: số tiền = `due.amountMinor` (nguồn sự thật DB). Cho gõ tay ⇒ user có thể
 * nhập lệch số với due ⇒ engine post CONTRIBUTION theo due nhưng user tưởng trả số mình gõ.
 */
export function buildDebtPayBehalfFields(dueOptions: readonly SlotOption[]): SlotFieldSpec[] {
  return [{ key: DEBT_DUE_KEY, type: "single_choice", required: true, options: [...dueOptions] }];
}

/** Validate DEBT-PAY-BEHALF: dueId có + ∈ options THẬT (due của người khác, chưa đóng). */
export function validateDebtPayBehalfForm(
  formState: FormState,
  dueOptions: readonly SlotOption[],
): DebtPayBehalfPayload {
  const memberDueId = assertPresent(formState[DEBT_DUE_KEY], DEBT_DUE_KEY);
  assertInOptions(
    memberDueId,
    dueOptions,
    "Khoản thu được chọn không nằm trong danh sách khoản có thể trả hộ.",
  );
  return { memberDueId };
}

// ─── DEBT-REMIND (@UC9443) ────────────────────────────────────────────────────────────────────────

/** Field DEBT-REMIND: chọn ĐỢT THU (collection) — service nhắc mọi người chưa đóng của đợt đó. */
export function buildDebtRemindFields(collectionOptions: readonly SlotOption[]): SlotFieldSpec[] {
  return [
    { key: DEBT_COLLECTION_KEY, type: "single_choice", required: true, options: [...collectionOptions] },
  ];
}

/** Validate DEBT-REMIND: collectionId có + ∈ options THẬT (đợt thu của quỹ actor có quyền nhắc). */
export function validateDebtRemindForm(
  formState: FormState,
  collectionOptions: readonly SlotOption[],
): DebtRemindPayload {
  const collectionId = assertPresent(formState[DEBT_COLLECTION_KEY], DEBT_COLLECTION_KEY);
  assertInOptions(
    collectionId,
    collectionOptions,
    "Đợt thu được chọn không nằm trong danh sách đợt thu của bạn.",
  );
  return { collectionId };
}

// ─── DEBT-CONFIRM (@UC9444) ───────────────────────────────────────────────────────────────────────

/**
 * Field DEBT-CONFIRM: chọn DÀN XẾP ĐANG CHỜ + accept/decline.
 * 🔴 2 PHÍA: options do service nạp = settlement mà ACTOR LÀ CHỦ NỢ (creditor) và đang `claimed`/
 * `transferred_unreconciled`. Con nợ KHÔNG tự accept được — engine (`:371`) chốt 403 lần nữa.
 */
export function buildDebtConfirmFields(settlementOptions: readonly SlotOption[]): SlotFieldSpec[] {
  return [
    {
      key: DEBT_SETTLEMENT_KEY,
      type: "single_choice",
      required: true,
      options: [...settlementOptions],
    },
    {
      key: DEBT_DECISION_KEY,
      type: "single_choice",
      required: true,
      options: [{ value: DEBT_DECISION_ACCEPT }, { value: DEBT_DECISION_DECLINE }],
    },
  ];
}

/** Nhãn quyết định (vi) — SERVER điền, KHÔNG do LLM bịa. */
export const DEBT_DECISION_LABELS_VI: Readonly<Record<string, string>> = Object.freeze({
  [DEBT_DECISION_ACCEPT]: "Xác nhận đã nhận tiền (ghi sổ là đã trả xong)",
  [DEBT_DECISION_DECLINE]: "Từ chối (chưa nhận được tiền)",
});

/**
 * Validate DEBT-CONFIRM: settlementId ∈ options THẬT (actor là chủ nợ, đang chờ) + decision ∈ enum.
 * KHÔNG suy quyết định từ câu chữ — user PHẢI chọn (accept = ghi sổ tiền, không đoán).
 */
export function validateDebtConfirmForm(
  formState: FormState,
  settlementOptions: readonly SlotOption[],
): DebtConfirmPayload {
  const settlementId = assertPresent(formState[DEBT_SETTLEMENT_KEY], DEBT_SETTLEMENT_KEY);
  assertInOptions(
    settlementId,
    settlementOptions,
    "Yêu cầu trả nợ được chọn không nằm trong danh sách bạn cần xác nhận.",
  );

  const decisionRaw = assertPresent(formState[DEBT_DECISION_KEY], DEBT_DECISION_KEY);
  const decisionValidation = validateFieldValue(
    {
      key: DEBT_DECISION_KEY,
      type: "single_choice",
      required: true,
      options: [{ value: DEBT_DECISION_ACCEPT }, { value: DEBT_DECISION_DECLINE }],
    },
    decisionRaw,
  );
  if (!decisionValidation.ok) {
    throw new DomainError("REQUIRED_FIELD_MISSING", decisionValidation.message);
  }
  const decision = decisionValidation.value;
  if (decision !== DEBT_DECISION_ACCEPT && decision !== DEBT_DECISION_DECLINE) {
    throw new DomainError("REQUIRED_FIELD_MISSING", "Quyết định không hợp lệ.");
  }
  return { settlementId, decision };
}
